"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import {
  ErrorBarChart,
  type ErrorBarStyle,
} from "@/components/tools/diagrams-more/error-bar-chart";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  type BarIssue,
  type ErrorMeasure,
  errorHalf,
  groupStats,
  groupsWithoutBar,
  parseRaw,
  parseSummary,
  usableLevel,
} from "@/lib/tool-kit/diagrams-more/error-bars";

/** English strings. A translated content file overrides these through `ui`. */
export const ERROR_BAR_CHART_MAKER_STRINGS = {
  modeLabel: "Your data is",
  modeSummary: "Summary statistics",
  modeRaw: "Raw values",
  summaryLabel: "Your groups — one per line: Group, mean, SD, n",
  summaryHint:
    "Write Control, 4.2, 1.1, 24 — the group's name, its mean, its sample standard deviation and its number of observations. A first line of column names is ignored. Use semicolons between cells if your numbers use decimal commas.",
  exampleSummary:
    "Group, Mean, SD, n\nControl, 4.2, 1.1, 24\nLow dose, 5.6, 1.3, 25\nHigh dose, 7.1, 1.6, 23",
  rawLabel: "Your groups — one per line: Name: value, value, …",
  rawHint:
    "Write Control: 3.1, 4.5, 3.8. The tool works out each group's mean, SD, SE and confidence interval from the values. A group needs at least 2 values for an SE or CI bar.",
  exampleRaw:
    "Control: 3.1, 4.5, 3.8, 5.2, 4.0, 4.9, 3.6, 4.4\nLow dose: 5.0, 6.2, 5.5, 4.8, 6.9, 5.7, 5.3, 6.1\nHigh dose: 7.4, 6.8, 8.1, 7.0, 6.5, 8.6, 7.7, 7.2",
  loadExample: "Load the example",
  exampleTitle: "Mean Response by Dose",
  exampleXTitle: "Group",
  exampleYTitle: "Mean response",
  measureLabel: "What the error bars show",
  measureSd: "Standard deviation (SD)",
  measureSe: "Standard error (SE)",
  measureCi: "Confidence interval (CI)",
  levelLabel: "Confidence level (%)",
  levelBad: "The confidence level must be between 50 and 99.9, so 95 was used.",
  styleLabel: "Draw the means as",
  styleBars: "Columns",
  styleDots: "Dots",
  hintSd:
    "SD bars show how spread out the individual values are. They do not shrink as the sample grows, and say little about whether two means differ.",
  hintSe:
    "SE bars show how precisely each mean is estimated. For groups of 10 or more, a 95% CI is roughly twice the length of an SE bar.",
  hintCi:
    "A CI is the range of means the data are compatible with. For independent groups of 10 or more, 95% CIs that overlap by about half of one arm correspond to p ≈ .05 (Cumming, Fidler & Vaux, 2007).",
  groupsDetected: "one:{count} group|other:{count} groups",
  enterData: "Enter at least one group to draw the chart.",
  noBar:
    "No error bar for {groups}: a standard error or confidence interval needs at least 2 values.",
  issues: {
    badRow:
      "“{text}” needs a mean, a standard deviation and a group size, so it was left out.",
    badSd:
      "The standard deviation for “{text}” must be 0 or more, so it was left out.",
    badN: "The group size for “{text}” must be a whole number from 1, so it was left out.",
    single: "“{text}” has a single value, so it has no spread to show.",
  },
  moreIssues: "one:…and {count} more notice.|other:…and {count} more notices.",
  barsSd: "Error bars: ±1 SD",
  barsSe: "Error bars: ±1 SE",
  barsCi: "Error bars: {level}% CI",
  noteHeading: "Suggested figure note",
  noteSd: "Error bars represent ±1 standard deviation.",
  noteSe: "Error bars represent ±1 standard error of the mean.",
  noteCi: "Error bars represent {level}% confidence intervals.",
  noteSizes: "Group sizes: {sizes}.",
  sizeItem: "{group}, n = {n}",
  useNote: "Use as the APA figure note",
  copyNote: "Copy note",
  groupColumn: "Group",
  nColumn: "n",
  meanColumn: "Mean",
  sdColumn: "SD",
  seColumn: "SE",
  ciLowerColumn: "{level}% CI lower",
  ciUpperColumn: "{level}% CI upper",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  xAxisTitle: "Horizontal axis title",
  yAxisTitle: "Vertical axis title",
  axisTitlePlaceholderX: "e.g. Group",
  axisTitlePlaceholderY: "e.g. Mean response",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabel: "Label each mean",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy statistics",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show every group's statistics as a table",
  chartLabel: "Error bar chart",
  chartDescription: "Chart of the mean of {groups}. {bars}. {list}.",
  describeGroup: "{group}: mean {mean}, bar ±{half}",
  describeGroupNoBar: "{group}: mean {mean}, no bar",
} as const;

type Strings = typeof ERROR_BAR_CHART_MAKER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Mode = "summary" | "raw";

const SHOWN_ISSUES = 3;

function Inputs({
  strings,
  mode,
  setMode,
  texts,
  setText,
}: {
  strings: Strings;
  mode: Mode;
  setMode: (mode: Mode) => void;
  texts: Record<Mode, string>;
  setText: (mode: Mode, text: string) => void;
}) {
  const summary = mode === "summary";
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.modeLabel}
        name="ebm-mode"
        onChange={setMode}
        options={[
          { value: "summary", label: strings.modeSummary },
          { value: "raw", label: strings.modeRaw },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={summary ? strings.summaryHint : strings.rawHint}
        id={`ebm-${mode}`}
        label={summary ? strings.summaryLabel : strings.rawLabel}
        onChange={(text) => setText(mode, text)}
        rows={6}
        value={texts[mode]}
      />
      <button
        className={BUTTON_SECONDARY}
        onClick={() =>
          setText(mode, summary ? strings.exampleSummary : strings.exampleRaw)
        }
        type="button"
      >
        {strings.loadExample}
      </button>
    </div>
  );
}

function Controls({
  strings,
  measure,
  setMeasure,
  level,
  setLevel,
  kind,
  setKind,
}: {
  strings: Strings;
  measure: ErrorMeasure;
  setMeasure: (measure: ErrorMeasure) => void;
  level: string;
  setLevel: (level: string) => void;
  kind: ErrorBarStyle;
  setKind: (kind: ErrorBarStyle) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-6 sm:grid-cols-2">
        <PillGroup
          label={strings.measureLabel}
          name="ebm-measure"
          onChange={setMeasure}
          options={[
            { value: "sd", label: strings.measureSd },
            { value: "se", label: strings.measureSe },
            { value: "ci", label: strings.measureCi },
          ]}
          value={measure}
        />
        <PillGroup
          label={strings.styleLabel}
          name="ebm-style"
          onChange={setKind}
          options={[
            { value: "bars", label: strings.styleBars },
            { value: "dots", label: strings.styleDots },
          ]}
          value={kind}
        />
      </div>
      {measure === "ci" ? (
        <div className="max-w-xs">
          <TextField
            id="ebm-level"
            label={strings.levelLabel}
            numeric
            onChange={setLevel}
            value={level}
          />
        </div>
      ) : null}
      <p className={HINT}>
        {
          { sd: strings.hintSd, se: strings.hintSe, ci: strings.hintCi }[
            measure
          ]
        }
      </p>
    </div>
  );
}

function issueText(strings: Strings, issue: BarIssue): string {
  return t(strings.issues[issue.kind], { text: issue.text });
}

function barLabelFor(
  strings: Strings,
  measure: ErrorMeasure,
  levelText: string
): string {
  if (measure === "sd") {
    return strings.barsSd;
  }
  return measure === "se"
    ? strings.barsSe
    : t(strings.barsCi, { level: levelText });
}

function noteFor(
  strings: Strings,
  measure: ErrorMeasure,
  levelText: string,
  sizes: string
): string {
  let sentence = t(strings.noteCi, { level: levelText });
  if (measure === "sd") {
    sentence = strings.noteSd;
  } else if (measure === "se") {
    sentence = strings.noteSe;
  }
  return `${sentence} ${t(strings.noteSizes, { sizes })}`;
}

export default function ErrorBarChartMakerWidget() {
  const strings = useToolUi(ERROR_BAR_CHART_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt: Format = useToolNumber();
  const [mode, setMode] = useState<Mode>("summary");
  const [texts, setTexts] = useState<Record<Mode, string>>({
    summary: strings.exampleSummary,
    raw: strings.exampleRaw,
  });
  const [measure, setMeasure] = useState<ErrorMeasure>("ci");
  const [level, setLevel] = useState("95");
  const [kind, setKind] = useState<ErrorBarStyle>("bars");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: strings.exampleYTitle,
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse =
    mode === "summary" ? parseSummary(texts.summary) : parseRaw(texts.raw);
  const typedLevel = parseNumberInput(level);
  const levelOk = usableLevel(typedLevel);
  const confidence = levelOk ?? 95;
  const levelText = fmt(confidence, { maximumFractionDigits: 1 });
  const stats = parse.groups.map((group) => groupStats(group, confidence));
  const number = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const missing = groupsWithoutBar(stats, measure);
  const shown = parse.issues.slice(0, SHOWN_ISSUES);
  const hidden = parse.issues.length - shown.length;
  const head = [
    strings.groupColumn,
    strings.nColumn,
    strings.meanColumn,
    strings.sdColumn,
    strings.seColumn,
    t(strings.ciLowerColumn, { level: levelText }),
    t(strings.ciUpperColumn, { level: levelText }),
  ];
  const rows = stats.map((entry) => [
    entry.group.label,
    fmt(entry.group.n),
    number(entry.group.mean),
    number(entry.group.sd),
    number(entry.se),
    number(entry.group.mean - entry.ciHalf),
    number(entry.group.mean + entry.ciHalf),
  ]);
  const barLabel = barLabelFor(strings, measure, levelText);
  const sizes = stats
    .map((entry) =>
      t(strings.sizeItem, { group: entry.group.label, n: fmt(entry.group.n) })
    )
    .join("; ");
  const suggested = noteFor(strings, measure, levelText, sizes);
  const describe = stats
    .map((entry) => {
      const half = errorHalf(entry, measure);
      const values = {
        group: entry.group.label,
        mean: number(entry.group.mean),
      };
      return half === null
        ? t(strings.describeGroupNoBar, values)
        : t(strings.describeGroup, { ...values, half: number(half) });
    })
    .join("; ");

  return (
    <div className="space-y-6">
      <Inputs
        mode={mode}
        setMode={setMode}
        setText={(which, text) =>
          setTexts((old) => ({ ...old, [which]: text }))
        }
        strings={strings}
        texts={texts}
      />
      <Controls
        kind={kind}
        level={level}
        measure={measure}
        setKind={setKind}
        setLevel={setLevel}
        setMeasure={setMeasure}
        strings={strings}
      />
      <div aria-live="polite" className="space-y-2">
        {stats.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.groupsDetected, locale, stats.length, {
              count: fmt(stats.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterData}</Notice>
        )}
        {measure === "ci" && levelOk === null ? (
          <Notice>{strings.levelBad}</Notice>
        ) : null}
        {missing.length > 0 ? (
          <Notice>{t(strings.noBar, { groups: missing.join(", ") })}</Notice>
        ) : null}
        {shown.map((issue) => (
          <Notice key={`${issue.kind}-${issue.text}`}>
            {issueText(strings, issue)}
          </Notice>
        ))}
        {hidden > 0 ? (
          <p className={HINT}>
            {tPlural(strings.moreIssues, locale, hidden, {
              count: fmt(hidden),
            })}
          </p>
        ) : null}
      </div>
      {stats.length > 0 ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([head, ...rows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "error-bar-chart")}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <ErrorBarChart
            barLabel={barLabel}
            description={t(strings.chartDescription, {
              groups: stats.map((entry) => entry.group.label).join(", "),
              bars: barLabel,
              list: describe,
            })}
            fmt={fmt}
            id="ebm-chart"
            kind={kind}
            label={style.title.trim() || strings.chartLabel}
            measure={measure}
            stats={stats}
            style={style}
          />
        </ChartFigure>
      ) : null}
      {stats.length > 0 ? (
        <div className="space-y-2 rounded-lg border border-border bg-card p-4">
          <p className={LABEL}>{strings.noteHeading}</p>
          <p className="text-foreground text-sm">{suggested}</p>
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => update({ note: suggested, apa: true })}
              type="button"
            >
              {strings.useNote}
            </button>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyNote}
              text={suggested}
            />
          </div>
        </div>
      ) : null}
      <ChartStyleFields
        axisLabels={{
          xAxisTitle: strings.xAxisTitle,
          yAxisTitle: strings.yAxisTitle,
          axisTitlePlaceholderX: strings.axisTitlePlaceholderX,
          axisTitlePlaceholderY: strings.axisTitlePlaceholderY,
        }}
        colorLabels={strings}
        idPrefix="ebm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabel}
      />
    </div>
  );
}
